
import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Header
    'header.title': 'KML Area Generator',
    'header.description': 'Draw areas on the map and generate KML files for download.',
    
    // Instructions
    'instructions.title': 'How to use:',
    'instructions.step1': 'Enter an address to navigate to a specific location (optional).',
    'instructions.step2': 'Find the map area you want to draw on. Hold space bar to move the map.',
    'instructions.step3': 'Click to draw the desired area on the map.',
    'instructions.step4': 'Download the KML file to use anywhere.',
    
    // Address Search
    'addressSearch.placeholder': 'Enter an address to search...',
    'addressSearch.searching': 'Searching...',
    'addressSearch.noResults': 'No results found',
    'addressSearch.found': 'Found',
    
    // Map Status
    'map.drawing': 'Drawing in progress...',
    'map.dragMode': 'Drag mode active (release spacebar to draw)',
    
    // Actions
    'actions.clearAll': 'Clear All',
    'actions.download': 'Download KML File',
    'actions.getEmbed': 'Get Embed Link',
    'actions.embedCopied': 'Embed link copied to clipboard!',
    
    // Status
    'status.noAreas': 'No areas drawn yet',
    'status.areasDrawn': 'area(s) drawn',
    
    // Footer
    'footer.description': 'KML files can be imported into Google Earth, Google Maps, and other mapping applications',
    'footer.perfectFor': 'Perfect for embedding in forms, surveys, and web applications.',
    
    // Main page
    'main.title': 'KML Area Generator',
    'main.description': 'Draw areas on an interactive map and generate KML files for use in Google Earth, Google Maps, and other mapping applications.',

    // Area
    'area.total': 'Total Area',
    'area.sqMeters': 'm²',
    'area.sqKilometers': 'km²',

    // Viewer
    'viewer.title': 'KML Area Viewer',
    'viewer.description': 'Upload and view KML files with area calculations.',
    'viewer.switchToGenerator': 'KML Area Generator',
    'viewer.uploadTitle': 'Upload a KML File',
    'viewer.uploadDescription': 'Select a KML file to view its areas on the map',
    'viewer.uploadButton': 'Upload KML File',
    'viewer.areasLoaded': 'area(s) loaded',
    'viewer.noFileLoaded': 'No KML file loaded',
    'viewer.uploadAnother': 'Upload another file',
    'viewer.footerDescription': 'Upload KML files generated by mapping applications or the KML Area Generator'
  },
  de: {
    // Header
    'header.title': 'KML-Generator',
    'header.description': 'Zeichnen Sie Bereiche auf der Karte und generieren Sie KML-Dateien zum Download.',
    
    // Instructions
    'instructions.title': 'So verwenden Sie es:',
    'instructions.step1': 'Geben Sie eine Adresse ein, um zu einem bestimmten Ort zu navigieren (optional).',
    'instructions.step2': 'Finden Sie den Kartenbereich, den Sie zeichnen möchten. Halten Sie die Leertaste gedrückt, um die Karte zu bewegen.',
    'instructions.step3': 'Klicken Sie, um den gewünschten Bereich auf der Karte zu zeichnen.',
    'instructions.step4': 'Laden Sie die KML-Datei herunter, um sie überall zu verwenden.',
    
    // Address Search
    'addressSearch.placeholder': 'Adresse eingeben zum Suchen...',
    'addressSearch.searching': 'Suchen...',
    'addressSearch.noResults': 'Keine Ergebnisse gefunden',
    'addressSearch.found': 'Gefunden',
    
    // Map Status
    'map.drawing': 'Zeichnung läuft...',
    'map.dragMode': 'Ziehen-Modus aktiv (Leertaste loslassen zum Zeichnen)',
    
    // Actions
    'actions.clearAll': 'Alles löschen',
    'actions.download': 'KML-Datei herunterladen',
    'actions.getEmbed': 'Einbettungslink abrufen',
    'actions.embedCopied': 'Einbettungslink in die Zwischenablage kopiert!',
    
    // Status
    'status.noAreas': 'Noch keine Bereiche gezeichnet',
    'status.areasDrawn': 'Bereich(e) gezeichnet',
    
    // Footer
    'footer.description': 'KML-Dateien können in Google Earth, Google Maps und andere Kartenanwendungen importiert werden',
    'footer.perfectFor': 'Perfekt für die Einbettung in Formulare, Umfragen und Webanwendungen.',
    
    // Main page
    'main.title': 'KML-Generator',
    'main.description': 'Zeichnen Sie Bereiche auf einer interaktiven Karte und generieren Sie KML-Dateien für die Verwendung in Google Earth, Google Maps und anderen Kartenanwendungen.',

    // Area
    'area.total': 'Gesamtfläche',
    'area.sqMeters': 'm²',
    'area.sqKilometers': 'km²',

    // Viewer
    'viewer.title': 'KML-Datei Betrachter',
    'viewer.description': 'Laden Sie KML-Dateien hoch und betrachten Sie sie mit Flächenberechnungen.',
    'viewer.switchToGenerator': 'KML-Generator',
    'viewer.uploadTitle': 'KML-Datei hochladen',
    'viewer.uploadDescription': 'Wählen Sie eine KML-Datei aus, um ihre Bereiche auf der Karte anzuzeigen',
    'viewer.uploadButton': 'KML-Datei hochladen',
    'viewer.areasLoaded': 'Bereich(e) geladen',
    'viewer.noFileLoaded': 'Keine KML-Datei geladen',
    'viewer.uploadAnother': 'Weitere Datei hochladen',
    'viewer.footerDescription': 'Laden Sie KML-Dateien hoch, die von Kartenanwendungen oder dem KML-Generator erstellt wurden'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    // Check URL parameters for language
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang') as Language;
    if (langParam && (langParam === 'en' || langParam === 'de')) {
      setLanguage(langParam);
    }
  }, []);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
